import { useEffect, useState, useRef } from 'react';
import { cn } from '../lib/utils';

type Variant = 'chapas' | 'medalhas' | 'trofeus' | 'tombstones' | 'placas';

const VARIANT_CONFIG: Record<Variant, { bg: string; text: string; glow: string; label: string }> = {
  chapas:     { bg: 'bg-blue-500',    text: 'text-blue-500',    glow: 'hover:shadow-[0_0_20px_rgba(59,130,246,0.15)]',  label: 'Chapas' },
  medalhas:   { bg: 'bg-amber-500',   text: 'text-amber-500',   glow: 'hover:shadow-[0_0_20px_rgba(245,158,11,0.15)]',  label: 'Medalhas' },
  trofeus:    { bg: 'bg-emerald-500', text: 'text-emerald-500', glow: 'hover:shadow-[0_0_20px_rgba(16,185,129,0.15)]',  label: 'Troféus' },
  tombstones: { bg: 'bg-purple-500',  text: 'text-purple-500',  glow: 'hover:shadow-[0_0_20px_rgba(147,51,234,0.15)]', label: 'Tombstones' },
  placas:     { bg: 'bg-teal-500',    text: 'text-teal-500',    glow: 'hover:shadow-[0_0_20px_rgba(20,184,166,0.15)]',  label: 'Placas' },
} as const;

interface NotificationBadgeProps {
  chapasCount: number;
  medalhasCount: number;
  trofeusCount?: number;
  tombstonesCount?: number;
  placasCount?: number;
  showChapas: boolean;
  showMedalhas: boolean;
  showTrofeus?: boolean;
  showTombstones?: boolean;
  showPlacas?: boolean;
  onChapasClick?: () => void;
  onMedalhasClick?: () => void;
  onTrofeusClick?: () => void;
  onTombstonesClick?: () => void;
  onPlacasClick?: () => void;
}

interface CounterPillProps {
  variant: Variant;
  count: number;
  onClick?: () => void;
}

// Chapas SVG icon - layered sheets/plates
const ChapasIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]">
    <rect x="3" y="6" width="18" height="13" rx="2.5"/>
    <path d="M5 6V5a2.5 2.5 0 0 1 2.5-2.5h9A2.5 2.5 0 0 1 19 5v1"/>
    <line x1="8" y1="10.5" x2="16" y2="10.5" opacity="0.4"/>
    <line x1="8" y1="14" x2="13" y2="14" opacity="0.4"/>
  </svg>
);

// Medalhas SVG icon - medal with ribbon
const MedalhasIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]">
    <circle cx="12" cy="14.5" r="5.5"/>
    <circle cx="12" cy="14.5" r="2.5" opacity="0.4"/>
    <path d="M8.5 9.5L7 2h3.5L12 4.5 13.5 2H17l-1.5 7.5"/>
  </svg>
);

// Trofeus SVG icon - trophy cup
const TrofeusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
    <path d="M4 22h16"/>
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 19.24 7 20v2"/>
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 19.24 17 20v2"/>
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
  </svg>
);

// Tombstones SVG icon - monument tablet with star
const TombstonesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]">
    <rect x="5" y="2" width="14" height="17" rx="2.5"/>
    <path d="M8 22h8"/>
    <path d="M10 19v3"/>
    <path d="M14 19v3"/>
    <path d="M12 7.5l1.1 2.2 2.4.35-1.75 1.7.4 2.4L12 13l-2.15 1.15.4-2.4-1.75-1.7 2.4-.35Z" opacity="0.4"/>
  </svg>
);

// Placas SVG icon - award plaque with laurel
const PlacasIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px]">
    <rect x="4" y="3" width="16" height="18" rx="2"/>
    <path d="M9 8h6" opacity="0.4"/>
    <path d="M9 11h6" opacity="0.4"/>
    <circle cx="12" cy="16" r="2"/>
  </svg>
);

// Rolling digit component
function RollingDigit({ digit, slotIndex }: { digit: number; slotIndex: number }) {
  const reelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reelRef.current) {
      // Stagger animation per digit slot (80ms per slot)
      const delay = slotIndex * 80;
      setTimeout(() => {
        if (reelRef.current) {
          reelRef.current.style.transform = `translateY(-${digit * 18}px)`;
        }
      }, delay);
    }
  }, [digit, slotIndex]);

  return (
    <div className="inline-block w-[9px] h-[18px] overflow-hidden relative">
      <div
        ref={reelRef}
        className="flex flex-col absolute top-0 left-0 w-full transition-transform duration-[800ms] ease-[cubic-bezier(0.23,1,0.32,1)]"
        style={{ willChange: 'transform' }}
      >
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
          <span
            key={d}
            className="block w-[9px] h-[18px] text-[13px] font-semibold text-white leading-[18px] text-center flex-shrink-0"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {d}
          </span>
        ))}
      </div>
    </div>
  );
}

// Rolling badge component
function RollingBadge({ count, variant }: { count: number; variant: Variant }) {
  const digits = count.toString().split('').map(d => parseInt(d));
  const { bg: bgColor } = VARIANT_CONFIG[variant];

  return (
    <div className={cn(
      "min-w-[22px] h-[22px] rounded-md inline-flex items-center justify-center gap-[1px] px-[5px] relative overflow-hidden",
      bgColor
    )}>
      {digits.map((digit, index) => (
        <RollingDigit key={`${digit}-${index}`} digit={digit} slotIndex={index} />
      ))}
    </div>
  );
}

// Counter pill component
function CounterPill({ variant, count, onClick }: CounterPillProps) {
  const [isPulsing, setIsPulsing] = useState(false);
  const prevCountRef = useRef(count);

  useEffect(() => {
    if (count > prevCountRef.current) {
      setIsPulsing(true);
      const timer = setTimeout(() => setIsPulsing(false), 200);
      prevCountRef.current = count;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = count;
  }, [count]);

  const { text: iconColor, glow: glowColor, label: variantLabel } = VARIANT_CONFIG[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 bg-muted/50 border border-border rounded-xl px-2 py-1.5 cursor-pointer",
        "transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
        "hover:border-white/10 hover:-translate-y-px active:translate-y-0 active:scale-[0.98]",
        "relative overflow-hidden select-none",
        glowColor,
        isPulsing && "scale-[1.03]"
      )}
      title={`${count} novo${count !== 1 ? 's' : ''} orçamento${count !== 1 ? 's' : ''} de ${variantLabel.toLowerCase()}`}
    >
      <div className={cn("w-5 h-5 flex items-center justify-center flex-shrink-0", iconColor)}>
        {variant === 'chapas' ? <ChapasIcon /> : variant === 'medalhas' ? <MedalhasIcon /> : variant === 'trofeus' ? <TrofeusIcon /> : variant === 'placas' ? <PlacasIcon /> : <TombstonesIcon />}
      </div>
      <span className="text-xs font-medium text-foreground tracking-[0.2px] whitespace-nowrap">
        {variantLabel}
      </span>
      <RollingBadge count={count} variant={variant} />
    </button>
  );
}

export function NotificationBadge({
  chapasCount,
  medalhasCount,
  trofeusCount = 0,
  tombstonesCount = 0,
  placasCount = 0,
  showChapas,
  showMedalhas,
  showTrofeus = false,
  showTombstones = false,
  showPlacas = false,
  onChapasClick,
  onMedalhasClick,
  onTrofeusClick,
  onTombstonesClick,
  onPlacasClick
}: NotificationBadgeProps) {
  const pills: { variant: Variant; count: number; show: boolean; onClick?: () => void }[] = [
    { variant: 'chapas', count: chapasCount, show: showChapas, onClick: onChapasClick },
    { variant: 'medalhas', count: medalhasCount, show: showMedalhas, onClick: onMedalhasClick },
    { variant: 'trofeus', count: trofeusCount, show: showTrofeus, onClick: onTrofeusClick },
    { variant: 'tombstones', count: tombstonesCount, show: showTombstones, onClick: onTombstonesClick },
    { variant: 'placas', count: placasCount, show: showPlacas, onClick: onPlacasClick },
  ];

  const visiblePills = pills.filter(p => p.show);

  return (
    <div className="flex items-center gap-2">
      {visiblePills.map((pill, i) => (
        <div key={pill.variant} className="flex items-center gap-2">
          {i > 0 && <div className="w-px h-7 bg-border" />}
          <CounterPill variant={pill.variant} count={pill.count} onClick={pill.onClick} />
        </div>
      ))}
    </div>
  );
}
